// @ts-nocheck
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    focalPoint = "focalPoint",
    heading = "heading",
    subheading = "subheading",
    addPrimaryButton = "addPrimaryButton",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    contentPadding = "contentPadding",
    addSecondaryButton = "addSecondaryButton",
    secondaryButtonLabel = "secondaryButtonLabel",
    secondaryButtonLink = "secondaryButtonLink",
    secondaryButtonVariant = "secondaryButtonVariant",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.imageOverlay]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.contentPadding]: number;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.addPrimaryButton]: boolean;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: ButtonVariants; // ButtonVariants needs to have "inverted" as well
        [fields.addSecondaryButton]: boolean;
        [fields.secondaryButtonLabel]: string;
        [fields.secondaryButtonLink]: LinkFieldValue;
        [fields.secondaryButtonVariant]: ButtonVariants; // ButtonVariants needs to have "inverted" as well
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
    };
    extendedStyles?: CustomBannerStyles;
}

export interface CustomBannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    hidden?: HiddenProps;
    mobileWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    bannerHeading?: TypographyPresentationProps;
    bannerSubHeading?: TypographyPresentationProps;
    secondaryButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const customBannerStyles: CustomBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            justify-content: left;
            text-align: left;
            align-items: center;
            width: 100%;
            color: ${getColor("primary.contrast")};
        `,
    },
    hidden: {
        css: css`
            width: 100%;
        `,
    },
    mobileWrapper: {
        css: css`
            color: ${getColor("primary.contrast")};
            background-color: ${getColor("primary.dark")};
            padding: ${pxToRem(16)};
        `,
    },
    imageWrapper: {
        css: css`
            ${breakpoint("xs", "down")} {
                margin-bottom: ${pxToRem(16)};
            }
        `,
    },
    img: {
        css: css`
            background-color: transparent;
            display: block;
            width: 100%;
            height: auto;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            ${breakpoint("sm", "down")} {
                width: 55%;
                flex-direction: column;
            }
        `,
    },
    bannerHeading: {
        css: css`
            display: block;
            margin-bottom: ${pxToRem(8)};
        `,
    },
    bannerSubHeading: {
        css: css`
            display: block;
            margin-bottom: ${pxToRem(16)};
        `,
    },
    bannerButton: {
        mergeCss: true,
    },
    secondaryButton: {
        mergeCss: true,
    },
};

export const CustomBanner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const secondaryLink = useGetLink(fields.secondaryButtonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const secondaryOnClick = () => {
        if (!secondaryLink.url) {
            return;
        }

        if (secondaryLink.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = secondaryLink.url;
        } else {
            history.push(secondaryLink.url);
        }
    };

    const backgroundStyles = `background-image: url(${fields.image});
           background-size: cover;`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    const overlayPositioningStyles = "align-items: stretch;";

    const styles = useMergeStyles("banner", customBannerStyles, extendedStyles);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const customCssWrapper = {
        css: css`
            height: inherit;
            ${fields.customCSS};
        `,
    };

    const wrapperStyles = {
        css: css`
            height: inherit;
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${overlayPositioningStyles}
            ${fontSizeStyles}
        `,
    };
    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background: linear-gradient(to right, ${fields.imageOverlay}, transparent 95%, transparent);
            display: flex;
            align-items: center;
            width: 45%;
            padding: ${fields.contentPadding}px;
        `,
    };

    const customHiddenCss = mergeProps<CustomBannerStyles["hidden"]>(styles.hidden, {
        css: css`
            height: inherit;
        `,
    });

    const bannerButtonProps = mergeProps<CustomBannerStyles["bannerButton"]>(styles.bannerButton, {
        css: css`
            ${() => {
                if ((fields.buttonVariant as any) === "secondary") {
                    return css`
                        &:hover {
                            background-color: ${getColor("secondary.light")};
                            color: ${getColor("primary.dark")};
                        }
                    `;
                }
            }}
        `,
    });

    const customSecondaryButtonProps = mergeProps<CustomBannerStyles["secondaryButton"]>(styles.secondaryButton, {
        css: css`
            ${breakpoint("md", "up")} {
                margin-left: ${pxToRem(16)};
            }
            ${breakpoint("sm", "down")} {
                margin-top: ${pxToRem(8)};
            }
            ${() => {
                if ((fields.secondaryButtonVariant as any) === "primary") {
                    return css`
                        border-color: ${getColor("primary.contrast")};
                        &:hover {
                            border-color: ${getColor("primary.contrast")};
                        }
                    `;
                }
            }}
        `,
    });

    const image = <Img className="callout-img" src={fields.image} loading="lazy" {...styles.img} />;

    const content = (
        <>
            <Typography {...styles.bannerHeading} className="banner-heading">
                {parse(fields.heading, parserOptions)}
            </Typography>
            <Typography {...styles.bannerSubHeading} className="banner-subheading">
                {parse(fields.subheading, parserOptions)}
            </Typography>
            {fields.addPrimaryButton && (
                <StyledWrapper {...styles.buttonWrapper}>
                    <Button {...bannerButtonProps} variant={fields.buttonVariant} onClick={onClick}>
                        {fields.buttonLabel || title || url}
                    </Button>
                    {fields.addSecondaryButton && (
                        <Button
                            {...customSecondaryButtonProps}
                            variant={fields.secondaryButtonVariant}
                            onClick={secondaryOnClick}
                        >
                            {fields.secondaryButtonLabel || secondaryLink.title || secondaryLink.url}
                        </Button>
                    )}
                </StyledWrapper>
            )}
        </>
    );

    return (
        <StyledWrapper {...customCssWrapper}>
            <Hidden above="xs" {...customHiddenCss}>
                <StyledWrapper {...styles.mobileWrapper}>
                    <StyledWrapper {...styles.imageWrapper}>{image}</StyledWrapper>
                    {content}
                </StyledWrapper>
            </Hidden>
            <Hidden below="sm" {...customHiddenCss}>
                <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                    <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                        <StyledWrapper className="banner-center-wrapper" {...styles.slideCenteringWrapperStyles}>
                            {content}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const customBanner: WidgetModule = {
    component: CustomBanner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        allowedContexts: ["HomePage"],
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.addPrimaryButton,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
                isVisible: widget => widget.fields.addPrimaryButton,
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
                isVisible: widget => widget.fields.addPrimaryButton,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                    { displayName: "Inverted", value: "inverted" },
                ],
                hideEmptyOption: true,
                defaultValue: "inverted",
                fieldType: "General",
                tab: basicTab,
                isVisible: widget => widget.fields.addPrimaryButton,
            },
            {
                fieldType: "General",
                name: fields.addSecondaryButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.secondaryButtonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                isVisible: widget => widget.fields.addSecondaryButton,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.secondaryButtonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isVisible: widget => widget.fields.addSecondaryButton,
                tab: basicTab,
            },
            {
                name: fields.secondaryButtonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                    { displayName: "Inverted", value: "inverted" },
                ],
                hideEmptyOption: true,
                defaultValue: "tertiary",
                fieldType: "General",
                isVisible: widget => widget.fields.addSecondaryButton,
                tab: basicTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default customBanner;
